import React, { useEffect, useRef, useState } from 'react';
import { Info, X } from 'lucide-react';
import { DEFS } from '../lib/viatrisDefinitions';

/**
 * DefinitionInfo — small (i) icon that toggles a popover with a
 * centralised definition from `viatrisDefinitions.DEFS`. Click-to-
 * toggle so it works on touch devices (hover-only tooltips lose
 * mobile users).
 *
 * Auto-flips its popover edge: when the icon is near the left edge
 * of the viewport, the popover opens to the right (left-0) so it
 * doesn't get clipped; otherwise it pins to the right edge of the
 * icon (right-0). No prop needed — the component measures itself
 * on open.
 *
 * Usage:
 *   <DefinitionInfo defKey="avg5" />
 *   <DefinitionInfo defKey="lock" size="md" />
 *
 * Props:
 *   - defKey: key into DEFS (required)
 *   - placement: 'auto' (default) | 'left' | 'right' — manual override
 *   - size: 'sm' (default 12px) | 'md' (14px) — info icon size
 *   - className: extra classes on the icon button
 */
export default function DefinitionInfo({ defKey, placement = 'auto', size = 'sm', className = '' }) {
  const [open, setOpen] = useState(false);
  const [effectivePlacement, setEffectivePlacement] = useState(placement === 'auto' ? 'right' : placement);
  const btnRef = useRef(null);
  const def = DEFS[defKey];

  // Auto-flip — measure once on open and decide which edge to pin the
  // popover to. The popover is ~320px wide and we ALWAYS want it to
  // open INTO the wider half of the screen, so for a button in the
  // left half of the viewport we anchor LEFT (popover opens
  // rightward); for a button in the right half we anchor RIGHT
  // (popover opens leftward). This handles both narrow-modal and
  // narrow-column cases without per-instance overrides.
  useEffect(() => {
    if (!open || placement !== 'auto' || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const buttonCenter = rect.left + rect.width / 2;
    const isLeftHalf = buttonCenter < window.innerWidth / 2;
    setEffectivePlacement(isLeftHalf ? 'left' : 'right');
  }, [open, placement]);

  if (!def) {
    // Fail soft — never crash the page if a defKey is misspelled.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[DefinitionInfo] Unknown defKey: ${defKey}`);
    }
    return null;
  }
  const iconSize = size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';
  const popoverEdge = effectivePlacement === 'left' ? 'left-0' : 'right-0';
  return (
    <span className="relative inline-block align-middle">
      <span
        ref={btnRef}
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); } }}
        className={`text-slate-400 hover:text-indigo-600 inline-flex items-center justify-center p-0.5 rounded-full hover:bg-indigo-50 transition-colors align-middle cursor-pointer ${className}`}
        title={def.title}
        data-testid={`def-info-${defKey}`}
        aria-label={`Definition: ${def.title}`}
        aria-expanded={open}
      >
        <Info className={iconSize} />
      </span>
      {open && (
        <>
          {/* Click-anywhere-to-close backdrop. */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            data-testid={`def-backdrop-${defKey}`}
          />
          <div
            className={`absolute ${popoverEdge} mt-1 w-80 max-w-[calc(100vw-2rem)] z-50 bg-white border border-indigo-200 rounded-lg shadow-xl p-3 text-left normal-case tracking-normal`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`def-popover-${defKey}`}
          >
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-semibold">
                {def.title}
              </div>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(false); } }}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
                aria-label="Close"
                data-testid={`def-close-${defKey}`}
              >
                <X className="w-3.5 h-3.5" />
              </span>
            </div>
            <p className="text-xs text-slate-700 leading-snug mb-2">{def.body}</p>
            {Array.isArray(def.math) && def.math.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 mb-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">How it&rsquo;s calculated</div>
                <ul className="list-disc pl-4 text-[11px] text-slate-700 space-y-0.5">
                  {def.math.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
            {def.example && (
              <div className="text-[11px] text-slate-600 italic border-l-2 border-indigo-200 pl-2">
                e.g. {def.example}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}
